import React from 'react';
import styled from 'styled-components';
import {Button, Detail, EMPHASIS, Text} from '@forecast-it/design-system';
import {
	FEATURE_FLAG_CARD_BORDER,
	FEATURE_FLAG_CARD_HOVER_BACKGROUND,
	FEATURE_FLAG_NEUTRAL_FILL,
	FEATURE_FLAG_WARNING_TEXT,
	featureFlagCardSurface,
} from '../featureFlagSurface';
import {MembershipRow} from './featureFlagLists';

// Every other card in this feature — including FeatureFlagAssignmentTable's equivalent table — gets
// its background, border and shadow from this shared surface. `overflow-x: auto` matches it too: the
// row grid's fixed-width tracks (150px, 170px, 44px) plus its 16px gaps give it a natural minimum
// width of a little over 600px, which a narrow viewport can still be short of even though this table
// declares no min-width of its own.
const TableCard = styled.div`
	${featureFlagCardSurface}
	overflow-x: auto;
`;

// Shared by the column header and the rows so the two can never drift apart, the same way
// FeatureFlagAssignmentTable shares its grid. The fourth track is the remove button's; it is dropped
// entirely — not merely emptied — when the table is read-only.
const WITH_REMOVE_COLUMNS = 'minmax(200px, 1.4fr) 150px 170px 44px';
const WITHOUT_REMOVE_COLUMNS = 'minmax(200px, 1.4fr) 150px 170px';
const GAP = '16px';

const ColumnHeader = styled.div<{$columns: string}>`
	display: grid;
	grid-template-columns: ${props => props.$columns};
	gap: ${GAP};
	padding: 9px 18px;
	background: ${FEATURE_FLAG_NEUTRAL_FILL};
	border-bottom: 1px solid ${FEATURE_FLAG_CARD_BORDER};
`;

// The tracking has to be declared on the child rather than here: the typography components emit
// their own letter-spacing onto the element they render, and a declaration on this wrapper would lose
// to it.
const MicroLabel = styled.span`
	> * {
		text-transform: uppercase;
		letter-spacing: 0.6px;
	}
`;

const Row = styled.div<{$columns: string}>`
	display: grid;
	grid-template-columns: ${props => props.$columns};
	gap: ${GAP};
	align-items: center;
	min-height: 44px;
	padding: 8px 18px;
	border-bottom: 1px solid ${FEATURE_FLAG_CARD_BORDER};

	&:hover {
		background: ${FEATURE_FLAG_CARD_HOVER_BACKGROUND};
	}
`;

const Cell = styled.span`
	display: flex;
	align-items: center;
	min-width: 0;
`;

// `Detail` accepts no warning colour (see FEATURE_FLAG_WARNING_TEXT), so the always-last note keeps
// Detail's own medium colour and this wrapper overrides it on the element Detail actually renders.
const NoteCell = styled(Cell)<{$warning: boolean}>`
	> * {
		color: ${props => (props.$warning ? FEATURE_FLAG_WARNING_TEXT : 'inherit')};
	}
`;

const ActionCell = styled.span`
	display: flex;
	justify-content: flex-end;
`;

const EmptyState = styled.div`
	display: flex;
	flex-direction: column;
	align-items: center;
	gap: 6px;
	padding: 48px 20px;
`;

export interface CompanyMembershipTableProps {
	rows: readonly MembershipRow[];
	/** Omit to render the table read-only: the remove column disappears entirely, not just its button. */
	onRemove?: (companyId: string) => void;
	emptyTitle: string;
	emptyHint: string;
}

export function CompanyMembershipTable({rows, onRemove, emptyTitle, emptyHint}: CompanyMembershipTableProps): JSX.Element {
	const columns = onRemove ? WITH_REMOVE_COLUMNS : WITHOUT_REMOVE_COLUMNS;

	return (
		<TableCard>
			<ColumnHeader $columns={columns} data-cy="membership-header">
				<MicroLabel>
					<Detail size="2">COMPANY</Detail>
				</MicroLabel>
				<MicroLabel>
					<Detail size="2">USERS</Detail>
				</MicroLabel>
				<MicroLabel>
					<Detail size="2">ALSO IN</Detail>
				</MicroLabel>
				{onRemove ? <span /> : null}
			</ColumnHeader>
			{rows.length === 0 ? (
				<EmptyState>
					<Text>{emptyTitle}</Text>
					<Detail color="medium">{emptyHint}</Detail>
				</EmptyState>
			) : (
				rows.map(row => (
					<Row key={row.companyId} $columns={columns} data-cy="membership-row">
						<Cell>
							<Text>{row.companyName}</Text>
						</Cell>
						<Cell>
							<Detail color="medium">{row.usersLabel}</Detail>
						</Cell>
						<NoteCell $warning={row.notesAlwaysLast}>
							<Detail color="medium">{row.note}</Detail>
						</NoteCell>
						{onRemove ? (
							<ActionCell>
								<Button
									emphasis={EMPHASIS.LOW}
									icon="trash"
									onPress={() => onRemove(row.companyId)}
									data-cy={`membership-remove-${row.companyId}`}
								/>
							</ActionCell>
						) : null}
					</Row>
				))
			)}
		</TableCard>
	);
}
